import {FC, Fragment, useEffect, useMemo, useState} from 'react'
import Swal from 'sweetalert2'
import {KTIcon} from '../../../_metronic/helpers'
import {FranchiseMachine, FranchiseMachineUnit} from '../core/_models'
import {getFranchiseMachines, syncFranchiseMachines} from '../core/_request'

type Props = {
  franchiseId: number
}

type LinkFilter = 'all' | 'linked' | 'unlinked'

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const unitLabel = (unit: FranchiseMachineUnit) => (unit.id ? `Unidade #${unit.id}` : 'Nova unidade')

export const FranchiseMachines: FC<Props> = ({franchiseId}) => {
  const [machines, setMachines] = useState<FranchiseMachine[]>([])
  const [original, setOriginal] = useState<FranchiseMachine[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all')

  const load = async () => {
    setLoading(true)
    try {
      const data = await getFranchiseMachines(franchiseId)
      setMachines(data)
      setOriginal(data)
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error?.response?.data?.message ?? 'Erro ao carregar as máquinas da franquia',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchiseId])

  const visible = useMemo(() => {
    const term = normalize(search)
    return machines.filter(
      (machine) =>
        normalize(machine.name).includes(term) &&
        (linkFilter === 'all' || (linkFilter === 'linked') === machine.units.length > 0)
    )
  }, [machines, search, linkFilter])

  const linkedCount = machines.filter((machine) => machine.units.length > 0).length
  const unitsCount = machines.reduce((total, machine) => total + machine.units.length, 0)

  const setUnits = (machineId: number, units: FranchiseMachineUnit[]) =>
    setMachines((current) =>
      current.map((machine) => (machine.id === machineId ? {...machine, units} : machine))
    )

  const addUnit = (machine: FranchiseMachine) =>
    setUnits(machine.id, [...machine.units, {id: null, rooms: []}])

  const removeUnit = (machine: FranchiseMachine, index: number) =>
    setUnits(
      machine.id,
      machine.units.filter((_, i) => i !== index)
    )

  const save = async () => {
    const currentIds = new Set(
      machines.flatMap((machine) => machine.units.map((unit) => unit.id)).filter(Boolean)
    )
    const removed = original.flatMap((machine) =>
      machine.units
        .filter((unit) => !currentIds.has(unit.id))
        .map((unit) => ({machine: machine.name, unit}))
    )
    const removedWithRooms = removed.filter(({unit}) => unit.rooms.length > 0)
    const created = machines.reduce(
      (total, machine) => total + machine.units.filter((unit) => !unit.id).length,
      0
    )

    const result = await Swal.fire({
      title: 'Deseja salvar as máquinas da franquia?',
      html:
        `${unitsCount} unidade(s) de ${linkedCount} máquina(s).` +
        (created
          ? `<br/>${created} unidade(s) nova(s): só poderão ser usadas na agenda após incluí-las em uma sala.`
          : '') +
        (removed.length ? `<br/>${removed.length} unidade(s) removida(s).` : '') +
        (removedWithRooms.length
          ? `<br/><br/><b>Unidades removidas que estavam em salas:</b><br/>` +
            removedWithRooms
              .map(({machine, unit}) => `${machine}: ${unitLabel(unit)} (${unit.rooms.join(', ')})`)
              .join('<br/>')
          : ''),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary',
      },
    })
    if (!result.isConfirmed) return

    setSaving(true)
    try {
      await syncFranchiseMachines(franchiseId, {
        machines: machines
          .filter((machine) => machine.units.length > 0)
          .map((machine) => ({
            id: machine.id,
            units: machine.units.map((unit) => ({id: unit.id})),
          })),
      })
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Salvo com sucesso',
        showConfirmButton: false,
        timer: 1500,
      })
      await load()
    } catch (error: any) {
      let text = error?.response?.data?.message
      if (error?.response?.status === 422) {
        text = Object.values(error.response.data.errors ?? {})
          .map((messages: any) => messages[0])
          .join('<br/>')
      }
      await Swal.fire({icon: 'error', title: 'Oops...', html: text})
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-bold mb-1'>Máquinas</h3>
          <span className='text-muted fs-7'>
            {unitsCount} unidade(s) de {linkedCount} de {machines.length} máquinas. Cada unidade é
            uma máquina física; as salas são definidas no cadastro de salas.
          </span>
        </div>
        <div className='card-toolbar gap-3'>
          <div className='position-relative'>
            <KTIcon
              iconName='magnifier'
              className='fs-3 position-absolute ms-4 top-50 translate-middle-y'
            />
            <input
              type='text'
              className='form-control form-control-solid w-225px ps-12'
              placeholder='Pesquisar máquina'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className='form-select form-select-solid w-175px'
            value={linkFilter}
            onChange={(e) => setLinkFilter(e.target.value as LinkFilter)}
          >
            <option value='all'>Todas</option>
            <option value='linked'>Com unidades</option>
            <option value='unlinked'>Sem unidades</option>
          </select>
        </div>
      </div>

      <div className='card-body py-4'>
        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-danger' />
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-3'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='min-w-200px'>Máquina</th>
                  <th className='text-center min-w-100px'>Unidades</th>
                  <th className='text-end min-w-125px'></th>
                </tr>
              </thead>
              <tbody className='text-gray-700 fw-semibold'>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={3} className='text-center text-muted py-8'>
                      Nenhuma máquina encontrada
                    </td>
                  </tr>
                )}
                {visible.map((machine) => (
                  <Fragment key={machine.id}>
                    <tr className={machine.units.length ? 'border-bottom-0' : undefined}>
                      <td className={machine.units.length ? 'text-gray-900 fw-bold' : 'text-muted'}>
                        {machine.name}
                      </td>
                      <td className='text-center'>
                        <span
                          className={`badge ${
                            machine.units.length ? 'badge-light-danger' : 'badge-light'
                          }`}
                        >
                          {machine.units.length}
                        </span>
                      </td>
                      <td className='text-end'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger'
                          onClick={() => addUnit(machine)}
                        >
                          <KTIcon iconName='plus' className='fs-4' />
                          Unidade
                        </button>
                      </td>
                    </tr>
                    {machine.units.map((unit, index) => (
                      <tr
                        key={unit.id ?? `new-${index}`}
                        className={index < machine.units.length - 1 ? 'border-bottom-0' : undefined}
                      >
                        <td colSpan={2} className='pt-0 ps-8'>
                          <div className='d-flex flex-wrap align-items-center gap-4'>
                            <span className='fs-7 fw-bold text-gray-800 min-w-100px'>
                              {unitLabel(unit)}
                            </span>
                            <span className='text-muted fs-7'>
                              {unit.rooms.length ? `Salas: ${unit.rooms.join(', ')}` : 'Sem sala'}
                            </span>
                          </div>
                        </td>
                        <td className='pt-0 text-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-icon btn-light-danger'
                            title='Remover unidade'
                            onClick={() => removeUnit(machine, index)}
                          >
                            <KTIcon iconName='trash' className='fs-4' />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='card-footer d-flex justify-content-end py-6 px-9'>
        <button
          type='button'
          className='btn btn-danger'
          disabled={loading || saving}
          onClick={save}
        >
          {!saving && 'Salvar máquinas'}
          {saving && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Salvando...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
